"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Upload, CheckCircle, Loader2, Clock, Mic, Square, Lock } from "lucide-react";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { aiService } from "../../apis/aiService";
import {
  applyLocalSessionProgress,
  getProgressForSession,
  isBusyStage,
} from "../../utils/transcriptionJobProgressStore";
import { authApiService } from "../../apis/authService";
// import {
//   audioTempChunkFileUrl,
//   withAudioTempSticky,
// } from "../../apis/tempAudioSticky";
import {
  useRecording,
  useRecordingLiveUi,
  acquireDisplayMediaStream,
  needsDisplayMediaCapture,
  stopMediaStream,
} from "../../hooks/useRecording";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import RecordingOptions from "./recording-options";
import { useAuth } from "../../context/AuthContext";
import { useTrial } from "../../context/TrialContext";
import { toast } from "react-hot-toast";
import {
  ensureWebSocketReady,
  setTranscriptionSocketCallbacks,
  clearTranscriptionSocketCallbacks,
  listenForTranscriptionJobStatus,
  removeTranscriptionJobStatusListeners,
  listenForTempAudioStatus,
  removeTempAudioStatusListeners,
} from "../../services/websocketService";
import { useAudioCache } from "../../hooks/useAudioCache";
import { isSoapGenerationFailure } from "../../utils/soapGenerationFailure";

const STAGES = {
  queued: "Queued",
  uploading: "Uploading",
  processing: "Processing",
  transcribing: "Transcribing",
  finalizing: "Finalizing",
  generating_soap: "Generating SOAP",
  completed: "Completed",
  failed: "Failed",
};

/** Stages where upload UI shows the progress card (idle card hidden). */
const UPLOAD_BUSY_STAGES = [
  "queued",
  "uploading",
  "processing",
  "transcribing",
  "finalizing",
  "generating_soap",
];

export default function AudioUploadTab({
  onTranscriptionComplete,
  onLoadingChange,
  setError,
  mode,
  setMode,
  sessionId,
  setTranscriptionId,
  setLiveTranscribed,
  setShowMaintenanceModal,
  onAudioUploaded,
  onUploadTranscriptionStreamState,
  onUploadTranscriptionStart,
  /** Current main tab — used to clear stale upload transcript when returning to this tab. */
  activeTab,
  onProgressChange,
  onSoapGenerationFailed,
  /** Visits are still loading/switching — keep every upload entry point disabled. */
  isSessionLoading = false,
}) {
  const { registerSessionAudio } = useAudioCache();
  const [transcript, setTranscript] = useState("");
  /**
   * Uploads currently in flight, keyed by session. This must NOT be a single boolean: this
   * component stays mounted while the user switches visits, so one visit's slow upload would
   * otherwise hide the upload button for every other visit.
   */
  const [uploadingSessions, setUploadingSessions] = useState({});
  const [loadingStage, setLoadingStage] = useState("");
  const [transcriptionProgress, setTranscriptionProgress] = useState({
    stage: "",
    percent: 0,
    message: "",
  });
  const fileInputRef = useRef(null);
  const transcriptionTimeoutRef = useRef(null);
  const currentJobIdRef = useRef(null);
  const progressNudgeRef = useRef(null);
  const websocketConnectedRef = useRef(false);
  const [websocketReady, setWebsocketReady] = useState(false);
  const [showAudio, setShowAudio] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [jobStatuses, setJobStatuses] = useState([]);
  const lastRehydratedJobUpdateAtRef = useRef(null);
  const lastProgressStageRef = useRef("");
  const onSoapGenerationFailedRef = useRef(onSoapGenerationFailed);
  useEffect(() => {
    onSoapGenerationFailedRef.current = onSoapGenerationFailed;
  }, [onSoapGenerationFailed]);
  /** Job IDs started from this tab for the current session (parallel uploads). */
  const sessionJobIdsRef = useRef(new Set());
  const uploadWsBagRef = useRef({});

  const sessionIdRef = useRef(sessionId);
  // Assign during render (not in an effect) so async callbacks always see the visit that is
  // on screen right now, even between a session switch and the next effect flush.
  sessionIdRef.current = sessionId;

  const uploadKey = (sid) => String(sid ?? "nosession");
  const isLoading = Boolean(uploadingSessions[uploadKey(sessionId)]);

  const setSessionUploading = useCallback((sid, value) => {
    const key = uploadKey(sid);
    setUploadingSessions((prev) => {
      if (Boolean(prev[key]) === Boolean(value)) return prev;
      const next = { ...prev };
      if (value) next[key] = true;
      else delete next[key];
      return next;
    });
  }, []);

  /** Loading flag for the visit currently on screen (used by websocket handlers). */
  const setIsLoading = useCallback(
    (value) => setSessionUploading(sessionIdRef.current, value),
    [setSessionUploading]
  );

  // Parent only needs to know whether ANY upload is running.
  const anyUploadActive = Object.keys(uploadingSessions).length > 0;
  const prevAnyUploadActiveRef = useRef(false);
  useEffect(() => {
    if (prevAnyUploadActiveRef.current === anyUploadActive) return;
    prevAnyUploadActiveRef.current = anyUploadActive;
    onLoadingChange?.(anyUploadActive);
  }, [anyUploadActive, onLoadingChange]);

  useEffect(() => {
    sessionJobIdsRef.current.clear();
  }, [sessionId]);

  /**
   * New visit / session: drop prior transcription preview so another chart's text cannot linger,
   * but restore this visit's own in-flight progress (e.g. an upload still running) if it has any.
   */
  useEffect(() => {
    setTranscript("");
    const saved = sessionId ? getProgressForSession(sessionId) : null;
    if (saved && isBusyStage(saved.stage)) {
      setTranscriptionProgress({
        stage: saved.stage,
        percent: saved.percent ?? 0,
        message: saved.message || "",
      });
    } else {
      setTranscriptionProgress({ stage: "", percent: 0, message: "" });
    }
    lastRehydratedJobUpdateAtRef.current = null;
    lastProgressStageRef.current = "";
  }, [sessionId]);

  const prevActiveTabRef = useRef(activeTab ?? null);

  /** Leaving another tab and landing on Upload: clear stale transcript unless a job is running. */
  useEffect(() => {
    const prev = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab ?? null;
    if (activeTab !== "upload") return;
    if (prev === activeTab) return;
    const busy =
      isLoading ||
      UPLOAD_BUSY_STAGES.includes(transcriptionProgress.stage || "");
    if (!busy) {
      setTranscript("");
    }
  }, [activeTab, isLoading, transcriptionProgress.stage]);

  const onTranscriptionCompleteRef = useRef(onTranscriptionComplete);
  useEffect(() => {
    onTranscriptionCompleteRef.current = onTranscriptionComplete;
  }, [onTranscriptionComplete]);

  const { currentUser } = useAuth();
  const { isAiFeatureLocked, getAccountLimitMessage } = useTrial();

  const blockIfAiLocked = () => {
    if (!isAiFeatureLocked) return false;
    toast.error(getAccountLimitMessage());
    return true;
  };

  /** Uploading while visits load could attach the audio to the wrong or not-yet-ready visit. */
  const blockIfSessionLoading = () => {
    if (!isSessionLoading) return false;
    toast("Visit is still loading — please wait a moment, then upload.", {
      id: "upload-session-loading",
    });
    return true;
  };
  /** Disable state for every upload entry point (AI lock or visits still loading). */
  const uploadDisabled = isAiFeatureLocked || isSessionLoading;
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const {
    isRecording,
    checkMicPermission,
    ensureDisplayAudio,
    getAudioInputDevices,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    isPaused,
    RecordingModalsGate,
    subscribeLiveUi,
    getLiveUiSnapshot,
  } = useRecording();
  const liveUi = useRecordingLiveUi({ subscribeLiveUi, getLiveUiSnapshot });
  const recordingTime = liveUi.recordingTime;

  const [recordingAudioMode, setRecordingAudioMode] = useState("input");
  const [recordingAudioInputDeviceId, setRecordingAudioInputDeviceId] = useState("");
  const [tempChunkIndex, setTempChunkIndex] = useState(0);
  const [hasTempRecording, setHasTempRecording] = useState(false);
  const [isManuallyPaused, setIsManuallyPaused] = useState(false);
  const [tempChunks, setTempChunks] = useState([]);
  const [combineStatus, setCombineStatus] = useState(null);
  const [hasCombinedFile, setHasCombinedFile] = useState(false);
  const [combinedFilename, setCombinedFilename] = useState(null);

  const isComputerAudioMode =
    recordingAudioMode === "output" || recordingAudioMode === "both";

  const tempAudioStateKey = (sid) =>
    `vbot_temp_audio_state:${sid || "nosession"}`;

  // On mount / session change, always check server temp state for this session
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sessionKey = sessionId || "nosession";

    // Reset in-memory pause UI when switching visits; restore chunk index per session.
    setIsManuallyPaused(false);
    try {
      const raw = localStorage.getItem(tempAudioStateKey(sessionKey));
      if (raw) {
        const parsed = JSON.parse(raw);
        setTempChunkIndex(
          parsed.sessionId === sessionKey ? Number(parsed.chunkIndex) || 0 : 0
        );
      } else {
        // Migrate legacy global key if it matches this session
        const legacy = localStorage.getItem("vbot_temp_audio_state");
        if (legacy) {
          const parsed = JSON.parse(legacy);
          if (parsed.sessionId === sessionKey) {
            setTempChunkIndex(Number(parsed.chunkIndex) || 0);
            localStorage.setItem(tempAudioStateKey(sessionKey), legacy);
          } else {
            setTempChunkIndex(0);
          }
        } else {
          setTempChunkIndex(0);
        }
      }
    } catch {
      setTempChunkIndex(0);
    }

    authApiService
      .get("/audio/temp/state", {
        params: { sessionId: sessionKey, patientId: "" },
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      })
      .then((res) => {
        const json = res.data;
        const ok = json?.success === true || json?.statusCode === true;
        const payload = json?.data || json?.body?.data;
        if (!ok || !payload) return;

        if (payload.chunkCount > 0) {
          setHasTempRecording(true);
          setTempChunks(payload.chunks || []);
          // Unfinished chunks mean this visit has a paused/segmented computer-audio capture.
          setIsManuallyPaused(true);
          const maxIdx = Math.max(
            0,
            ...(payload.chunks || [])
              .map((c) => Number(c.chunkIndex))
              .filter((n) => Number.isFinite(n))
          );
          if (Number.isFinite(maxIdx)) {
            const next = maxIdx + 1;
            setTempChunkIndex(next);
            localStorage.setItem(
              tempAudioStateKey(sessionKey),
              JSON.stringify({
                sessionId: sessionKey,
                patientId: "",
                chunkIndex: next,
              })
            );
          }
        } else {
          setHasTempRecording(false);
          setTempChunks([]);
        }

        if (payload.hasCombinedFile && payload.combinedFilename) {
          setHasCombinedFile(true);
          setCombinedFilename(payload.combinedFilename);
        } else {
          setHasCombinedFile(false);
          setCombinedFilename(null);
        }
      })
      .catch(() => {});
  }, [sessionId]);

  const upsertJobStatus = (partial) => {
    if (!partial?.jobId) return;
    setJobStatuses((prev) => {
      const next = [...prev];
      const idx = next.findIndex((item) => item.jobId === partial.jobId);
      if (idx === -1) {
        next.unshift({
          jobId: partial.jobId,
          status: partial.status || "queued",
          stage: partial.stage || "queued",
          percent: partial.percent ?? 0,
          message: partial.message || "Queued",
          updatedAt: partial.updatedAt || new Date().toISOString(),
          fileName: partial.fileName || null,
          queuePosition: partial.queuePosition ?? null,
          sessionId: partial.sessionId || null,
          chunkIndex: partial.chunkIndex ?? null,
          totalChunks: partial.totalChunks ?? null,
          transcriptChars: partial.transcriptChars ?? null,
          soapGenerationFailed: partial.soapGenerationFailed || false,
          transcriptionId: partial.transcriptionId || null,
          transcriptionText: partial.transcriptionText || "",
        });
      } else {
        next[idx] = {
          ...next[idx],
          ...partial,
          updatedAt: partial.updatedAt || new Date().toISOString(),
        };
      }
      return next
        .sort(
          (a, b) =>
            new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime(),
        )
        .slice(0, 40);
    });
  };

  useEffect(() => {
    const userId = currentUser?.userId ?? currentUser?.userid ?? currentUser?.id;
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const ready = await ensureWebSocketReady(userId, token || undefined);
        if (!cancelled) {
          websocketConnectedRef.current = ready;
          setWebsocketReady(ready);
        }
      } catch {
        if (!cancelled) {
          websocketConnectedRef.current = false;
          setWebsocketReady(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, token]);

  const hasActiveJobs =
    UPLOAD_BUSY_STAGES.includes(transcriptionProgress.stage || "") ||
    jobStatuses.some(
      (j) =>
        UPLOAD_BUSY_STAGES.includes(j.stage || "") ||
        j.status === "queued" ||
        j.status === "processing"
    );

  useEffect(() => {
    let cancelled = false;
    const pollJobs = async () => {
      try {
        const response = await aiService.getTranscriptionJobs({ limit: 30 });
        const jobs = response?.data?.body?.data?.jobs || response?.data?.data?.jobs || [];
        if (!cancelled && Array.isArray(jobs)) {
          const rows = jobs.map((job) => ({
            jobId: job.jobId,
            status: job.status,
            stage: job.stage,
            percent: job.percent ?? 0,
            message: job.message || STAGES[job.stage] || "",
            updatedAt: job.updatedAt,
            fileName: job.fileName || job.metadata?.originalFilename || null,
            queuePosition: job.queuePosition ?? null,
            sessionId: job.sessionId || null,
            soapGenerationFailed:
              job.soapGenerationFailed === true || isSoapGenerationFailure(job),
            transcriptionId: job.result?.transcriptionId || job.transcriptionId || null,
          }));
          setJobStatuses((prev) => {
            const map = new Map();
            for (const j of prev) map.set(j.jobId, { ...j });
            for (const row of rows) {
              const ex = map.get(row.jobId);
              if (!ex) {
                map.set(row.jobId, row);
                continue;
              }
              const tR = new Date(row.updatedAt || 0).getTime();
              const tE = new Date(ex.updatedAt || 0).getTime();
              const merged = tR >= tE ? { ...ex, ...row } : { ...row, ...ex };
              merged.soapGenerationFailed = Boolean(
                row.soapGenerationFailed || ex.soapGenerationFailed
              );
              merged.transcriptionId =
                row.transcriptionId || ex.transcriptionId || null;
              merged.transcriptionText =
                ex.transcriptionText || row.transcriptionText || "";
              map.set(row.jobId, merged);
            }
            return Array.from(map.values())
              .sort(
                (a, b) =>
                  new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime(),
              )
              .slice(0, 40);
          });
        }
      } catch (_) {
        // ignore polling failures; websocket events still drive updates
      }
    };

    // Always hydrate once so previously queued/in-progress jobs are visible
    // even when websocket is already connected and no new event arrives.
    pollJobs();

    // Active jobs: poll often so missed WS events still feel live.
    // Idle: slow background sync. Offline WS: medium poll until reconnect.
    const intervalMs = hasActiveJobs ? 2500 : websocketReady ? 30000 : 8000;
    const interval = setInterval(pollJobs, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [websocketReady, hasActiveJobs]);

  useEffect(() => {
    listenForTranscriptionJobStatus((jobData) => {
      const upsert = uploadWsBagRef.current.upsertJobStatus;
      if (typeof upsert !== "function") return;
      upsert({
        jobId: jobData.jobId,
        status: jobData.status || (jobData.stage === "queued" ? "queued" : "processing"),
        stage: jobData.stage || "processing",
        percent: jobData.percent ?? 0,
        message: jobData.message || STAGES[jobData.stage] || "Processing",
        updatedAt: jobData.updatedAt,
        fileName: jobData.fileName || null,
        queuePosition: jobData.queuePosition ?? null,
        sessionId: jobData.sessionId || null,
        chunkIndex: jobData.chunkIndex ?? null,
        totalChunks: jobData.totalChunks ?? null,
        transcriptChars: jobData.transcriptChars ?? null,
        soapGenerationFailed:
          jobData.soapGenerationFailed === true || isSoapGenerationFailure(jobData),
        transcriptionId: jobData.transcriptionId || jobData.result?.transcriptionId || null,
      });
    });

    return () => {
      removeTranscriptionJobStatusListeners();
    };
  }, []);

  useEffect(() => {
    if (!websocketReady) return;
    listenForTempAudioStatus({
      onCombineStart: () =>
        setCombineStatus("Preparing combined audio file from chunks..."),
      onCombineDone: () =>
        setCombineStatus("Combined audio file is ready for upload."),
    });

    return () => {
      removeTempAudioStatusListeners();
    };
  }, [websocketReady]);

  uploadWsBagRef.current = {
    upsertJobStatus,
    onTranscriptionCompleteRef,
    setTranscriptionProgress,
    setTranscript,
    setTranscriptionId,
    setError,
    setIsLoading,
    onLoadingChange,
    progressNudgeRef,
    transcriptionTimeoutRef,
    currentJobIdRef,
    sessionIdRef,
    sessionJobIdsRef,
    lastProgressStageRef,
    onSoapGenerationFailedRef,
  };

  useEffect(() => {
    // Keep callbacks registered even while reconnecting so early progress events are not dropped.
    const shouldApplyProgressForJob = (data, activeSessionId) => {
      const payloadSessionId = data.sessionId ?? null;
      // Jobs with a session id (live SOAP + uploads) update the open visit's progress.
      if (payloadSessionId != null && payloadSessionId !== "") {
        return String(payloadSessionId) === String(activeSessionId);
      }
      const id = data.jobId;
      const tracked = uploadWsBagRef.current.sessionJobIdsRef.current;
      if (!id) return true;
      if (tracked.size === 0) return true;
      return tracked.has(id);
    };

    setTranscriptionSocketCallbacks({
      onProgress: (data) => {
        const b = uploadWsBagRef.current;
        const activeSessionId = b.sessionIdRef.current;
        const payloadSessionId = data.sessionId ?? null;
        const jobMatchesActiveSession =
          payloadSessionId == null ||
          String(payloadSessionId) === String(activeSessionId);

        if (jobMatchesActiveSession && data.jobId) {
          b.currentJobIdRef.current = data.jobId;
        }
        b.upsertJobStatus({
          jobId: data.jobId,
          status: data.stage === "queued" ? "queued" : "processing",
          stage: data.stage || "processing",
          percent: data.percent ?? 0,
          message: data.message || STAGES[data.stage] || "Processing",
          sessionId: data.sessionId ?? null,
          chunkIndex: data.chunkIndex ?? null,
          totalChunks: data.totalChunks ?? null,
          transcriptChars: data.transcriptChars ?? null,
        });
        if (b.progressNudgeRef.current) {
          clearInterval(b.progressNudgeRef.current);
          b.progressNudgeRef.current = null;
        }
        if (shouldApplyProgressForJob(data, activeSessionId)) {
          if (data.stage) {
            b.lastProgressStageRef.current = data.stage;
          }
          b.setTranscriptionProgress((prev) => ({
            stage: data.stage || prev.stage || "processing",
            percent: data.percent ?? prev.percent,
            message: data.message || STAGES[data.stage] || prev.message || "",
          }));
        }
      },
      onStream: () => {
        // Live chunk lines are not shown on the upload tab (progress bar + % only).
      },
      onComplete: (data) => {
        const b = uploadWsBagRef.current;
        if (data.jobId) {
          b.sessionJobIdsRef.current.delete(data.jobId);
        }
        const inner = data?.data ?? data;
        const payload =
          inner &&
          typeof inner === "object" &&
          inner.data &&
          typeof inner.data === "object" &&
          (inner.data.soapNoteId != null ||
            inner.data.sessionId != null ||
            inner.data.transcriptionId != null)
            ? { ...inner, ...inner.data }
            : inner;
        b.upsertJobStatus({
          jobId: data.jobId,
          status: "completed",
          stage: "completed",
          percent: 100,
          message: "Completed",
          sessionId: payload?.sessionId ?? null,
        });
        const activeSessionId = b.sessionIdRef.current;
        const payloadSessionId = payload?.sessionId ?? null;
        const jobMatchesActiveSession =
          payloadSessionId == null ||
          String(payloadSessionId) === String(activeSessionId);

        if (b.progressNudgeRef.current) {
          clearInterval(b.progressNudgeRef.current);
          b.progressNudgeRef.current = null;
        }

        if (
          b.transcriptionTimeoutRef.current &&
          jobMatchesActiveSession &&
          b.sessionJobIdsRef.current.size === 0
        ) {
          clearTimeout(b.transcriptionTimeoutRef.current);
          b.transcriptionTimeoutRef.current = null;
        }

        const transcription =
          payload?.details?.fullTranscript ??
          payload?.transcriptionText ??
          payload?.transcription ??
          "";

        const resolvedSoapNoteId =
          payload?.soapNoteId ??
          payload?.metadata?.soapNoteId ??
          payload?.soapNote?.soapNoteId ??
          data?.soapNoteId ??
          data?.data?.soapNoteId ??
          null;

        b.onTranscriptionCompleteRef.current(
          payload?.conversation,
          payload?.conversation,
          payloadSessionId,
          resolvedSoapNoteId,
          payload?.patientName ?? null,
          data?.jobId ?? null
        );

        if (jobMatchesActiveSession) {
          b.setTranscript(transcription);
          b.setTranscriptionId(payload?.transcriptionId);
          b.setTranscriptionProgress({
            stage: "completed",
            percent: 100,
            message: "Completed",
          });
        }

        if (jobMatchesActiveSession) {
          b.setIsLoading(false);
        }
      },
      onError: (data) => {
        const b = uploadWsBagRef.current;
        const inTracked =
          !data.jobId ||
          b.sessionJobIdsRef.current.size === 0 ||
          b.sessionJobIdsRef.current.has(data.jobId);
        if (data.jobId) {
          b.sessionJobIdsRef.current.delete(data.jobId);
        }
        const activeSessionId = b.sessionIdRef.current;
        const payloadSessionId = data.sessionId ?? null;
        const jobMatchesActiveSession =
          payloadSessionId == null ||
          String(payloadSessionId) === String(activeSessionId);
        const soapFailed =
          isSoapGenerationFailure(data) ||
          b.lastProgressStageRef.current === "generating_soap";
        b.upsertJobStatus({
          jobId: data.jobId,
          status: "failed",
          stage: "failed",
          percent: 0,
          message: data.error || "Failed",
          sessionId: data.sessionId ?? null,
          soapGenerationFailed: soapFailed,
          transcriptionId: data.transcriptionId || null,
          transcriptionText:
            data.transcriptionText ||
            data.transcription ||
            data.fullTranscript ||
            "",
        });
        if (b.progressNudgeRef.current) {
          clearInterval(b.progressNudgeRef.current);
          b.progressNudgeRef.current = null;
        }
        if (b.transcriptionTimeoutRef.current) {
          clearTimeout(b.transcriptionTimeoutRef.current);
          b.transcriptionTimeoutRef.current = null;
        }
        const message = data.error || "Transcription failed";
        if (jobMatchesActiveSession && (inTracked || soapFailed)) {
          b.setIsLoading(false);
          b.setTranscriptionProgress({
            stage: "failed",
            percent: 0,
            message: data.error || "Failed",
          });
          b.lastProgressStageRef.current = soapFailed ? "" : "failed";
          if (soapFailed) {
            const transcriptionText =
              data.transcriptionText ||
              data.transcription ||
              data.fullTranscript ||
              "";
            if (transcriptionText) {
              b.setTranscript(transcriptionText);
            }
            if (data.transcriptionId) {
              b.setTranscriptionId(data.transcriptionId);
            }
            b.setError(null);
            b.onSoapGenerationFailedRef.current?.({
              transcriptionId: data.transcriptionId || null,
              transcriptionText,
              sessionId: data.sessionId || payloadSessionId || null,
            });
          } else {
            b.setError(message);
          }
        }
      },
    });

    return () => {
      clearTranscriptionSocketCallbacks();
    };
  }, []);

  // Rehydrate progress UI when user switches sessions
  // (especially when a job already progressed/completed while user was on another session).
  useEffect(() => {
    if (!sessionId) return;

    const activeSessionId = String(sessionId);
    const matchingJobs = jobStatuses
      .filter((j) => j?.sessionId != null && String(j.sessionId) === activeSessionId)
      .sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0));

    if (!matchingJobs.length) return;

    const latest = matchingJobs[0];
    const latestUpdateAt = latest?.updatedAt ?? null;
    const rehydrateKey = `${latest?.jobId ?? ""}:${latestUpdateAt ?? ""}`;
    if (rehydrateKey && lastRehydratedJobUpdateAtRef.current === rehydrateKey) {
      return;
    }
    lastRehydratedJobUpdateAtRef.current = rehydrateKey;

    setTranscriptionProgress({
      stage: latest.stage || (latest.status === "queued" ? "queued" : "processing"),
      percent: latest.percent ?? 0,
      message: latest.message || STAGES[latest.stage] || "Processing",
    });

    if (
      latest.status === "failed" &&
      (latest.soapGenerationFailed || isSoapGenerationFailure(latest))
    ) {
      onSoapGenerationFailedRef.current?.({
        transcriptionId: latest.transcriptionId || null,
        transcriptionText: latest.transcriptionText || "",
        sessionId: latest.sessionId || activeSessionId,
      });
    }
  }, [jobStatuses, sessionId]);

  const handleFileUpload = async (file) => {
    if (!file) return;
    if (blockIfAiLocked()) return;
    if (blockIfSessionLoading()) return;

    if (process.env.REACT_APP_MAINTENANCE_MODE === "true") {
      setError("Audio transcription is currently under maintenance. Please try again later.");
      setShowMaintenanceModal(true);
      return;
    }

    const userId = currentUser?.userId ?? currentUser?.userid ?? currentUser?.id;
    if (!currentUser || !userId) {
      setError("Session not ready for live progress. Please sign in or refresh the page and try again.");
      return;
    }

    // Everything below runs across awaits (the upload can take minutes). Pin the visit this
    // upload belongs to; the user may switch to another visit before it finishes.
    const uploadSessionId = sessionId;
    const isUploadSessionActive = () =>
      String(sessionIdRef.current ?? "") === String(uploadSessionId ?? "");

    setSessionUploading(uploadSessionId, true);
    setTranscript("");
    setError(null);
    lastProgressStageRef.current = "uploading";
    setTranscriptionProgress({ stage: "uploading", percent: 0, message: "Preparing upload…" });
    if (uploadSessionId) {
      applyLocalSessionProgress(uploadSessionId, {
        active: true,
        stage: "uploading",
        percent: 2,
        message: "Uploading your recording…",
      });
    }
    onUploadTranscriptionStart?.();

    // Show play controls on the session card immediately (before transcription/SOAP).
    if (uploadSessionId) {
      registerSessionAudio(uploadSessionId, file);
    }

    let lastReportedUploadPercent = -1;
    const handleUploadProgress = (evt) => {
      const total = Number(evt?.total) || file.size || 0;
      const loaded = Number(evt?.loaded) || 0;
      if (!total) return;
      const pct = Math.min(100, Math.round((loaded / total) * 100));
      if (pct === lastReportedUploadPercent) return;
      lastReportedUploadPercent = pct;
      const mb = (n) => (n / (1024 * 1024)).toFixed(1);
      const message =
        pct >= 100
          ? "Upload complete — waiting for the server to start processing…"
          : `Uploading your recording… ${pct}% (${mb(loaded)} / ${mb(total)} MB)`;
      // Overall bar stays in the 2–5% band: the real upload % is shown in the message.
      const barPercent = Math.max(2, Math.min(5, 2 + Math.floor(pct / 34)));
      if (uploadSessionId) {
        applyLocalSessionProgress(uploadSessionId, {
          active: true,
          stage: "uploading",
          percent: barPercent,
          message,
        });
      }
      if (isUploadSessionActive()) {
        setTranscriptionProgress({ stage: "uploading", percent: barPercent, message });
      }
    };

    try {
      if (progressNudgeRef.current) {
        clearInterval(progressNudgeRef.current);
        progressNudgeRef.current = null;
      }

      // Do not block the upload API on socket handshake — connect in parallel.
      if (isUploadSessionActive()) {
        setTranscriptionProgress({ stage: "uploading", percent: 5, message: "Uploading…" });
      }
      void ensureWebSocketReady(userId, token || undefined)
        .then((ready) => {
          websocketConnectedRef.current = ready;
          setWebsocketReady(ready);
        })
        .catch(() => {
          websocketConnectedRef.current = false;
          setWebsocketReady(false);
        });

      if (transcriptionTimeoutRef.current) {
        clearTimeout(transcriptionTimeoutRef.current);
        transcriptionTimeoutRef.current = null;
      }
      transcriptionTimeoutRef.current = setTimeout(() => {
        if (progressNudgeRef.current) {
          clearInterval(progressNudgeRef.current);
          progressNudgeRef.current = null;
        }
        if (isUploadSessionActive()) {
          setError("Transcription timed out. Please try again.");
        }
        setSessionUploading(uploadSessionId, false);
        transcriptionTimeoutRef.current = null;
      }, 2 * 60 * 60 * 1000); // 2 hours max wait for transcription result

      const result = await aiService.transcribeAudioWithSoapAsync(file, uploadSessionId, {
        onUploadProgress: handleUploadProgress,
      });
      // Background reconcile with S3 (optimistic entry already registered above).
      if (typeof onAudioUploaded === "function") {
        onAudioUploaded(uploadSessionId, file);
      }

      if (result.sync && result.data) {
        const data = result.data?.body?.data ?? result.data?.data ?? result.data;
        const transcription =
          data?.details?.text ?? data?.transcription ?? data?.details?.fullTranscript ?? "";
        const resolvedSoapNoteId =
          data?.soapNoteId ??
          data?.metadata?.soapNoteId ??
          data?.soapNote?.soapNoteId ??
          null;
        onTranscriptionCompleteRef.current(
          data?.conversation,
          data?.conversation,
          data?.sessionId ?? uploadSessionId ?? null,
          resolvedSoapNoteId,
          data?.patientName ?? null,
          null
        );
        if (isUploadSessionActive()) {
          setTranscript(transcription);
          setTranscriptionId(data?.transcriptionId);
          setTranscriptionProgress({ stage: "completed", percent: 100, message: "Transcript ready" });
        }
        if (uploadSessionId) {
          applyLocalSessionProgress(uploadSessionId, {
            active: false,
            stage: "completed",
            percent: 100,
            message: "Transcript ready",
          });
        }
        setSessionUploading(uploadSessionId, false);
        // Cleanup temp audio folder after successful transcription
        try {
          const clearSessionKey = uploadSessionId || "nosession";
          await authApiService.post(
            "/audio/temp/clear",
            {
              sessionId: sessionId || "nosession",
              patientId: "",
            },
            {
              headers: { Authorization: `Bearer ${token || ""}` },
            }
          );
        } catch (e) {
          console.warn("Failed to clear temp audio after transcription:", e);
        }
        return;
      }

      const jobId = result.jobId;
      if (!jobId) {
        throw new Error("Transcription started but no job ID returned");
      }
      if (isUploadSessionActive()) {
        sessionJobIdsRef.current.add(jobId);
        currentJobIdRef.current = jobId;
      }
      const queuedMessage =
        result.status === "queued"
          ? `In queue — transcription will start shortly${result.queuePosition ? ` (position ${result.queuePosition})` : ""}.`
          : "Processing your recording…";
      upsertJobStatus({
        jobId,
        status: result.status || "queued",
        stage: result.status === "processing" ? "processing" : "queued",
        percent: result.status === "processing" ? 1 : 0,
        message:
          result.status === "queued" ? queuedMessage : "Starting transcription…",
        queuePosition: result.queuePosition ?? null,
        sessionId: uploadSessionId || null,
        fileName: file.name || null,
      });
      if (uploadSessionId) {
        applyLocalSessionProgress(uploadSessionId, {
          jobId,
          active: true,
          stage: result.status === "queued" ? "queued" : "processing",
          percent: result.status === "queued" ? 0 : 5,
          message: queuedMessage,
        });
      }
      if (isUploadSessionActive()) {
        setTranscriptionProgress({
          stage: result.status === "queued" ? "queued" : "processing",
          percent: result.status === "queued" ? 0 : 5,
          message: queuedMessage,
        });
      }

      // Background mode: allow users to immediately continue with another patient
      setSessionUploading(uploadSessionId, false);
      return;
    } catch (error) {
      console.error("Transcription error:", error);
      const status = error.response?.status;
      const isTimeout = status === 408 || error.code === "ECONNABORTED";
      const message = isTimeout
        ? "Transcription timed out. The recording may be too long or the server is busy. Try a shorter recording or try again in a few minutes."
        : status === 413
          ? "Transcription failed: the audio file is too large to upload."
          : `Transcription failed: ${error.message}`;

      if (transcriptionTimeoutRef.current) {
        clearTimeout(transcriptionTimeoutRef.current);
        transcriptionTimeoutRef.current = null;
      }
      // Leave the visit in a retryable state. Without this the card stayed on
      // "Uploading… 2%" forever and the upload button never came back.
      if (uploadSessionId) {
        applyLocalSessionProgress(uploadSessionId, {
          active: false,
          stage: "failed",
          percent: 0,
          message,
        });
      }
      if (isUploadSessionActive()) {
        lastProgressStageRef.current = "failed";
        setTranscriptionProgress({ stage: "failed", percent: 0, message });
        setError(message);
      } else {
        // Finished while the user was on another visit — don't paint it onto that visit.
        toast.error(`Audio upload failed for the previous visit: ${message}`);
      }
      setSessionUploading(uploadSessionId, false);
    }
  };

  const handleUploadCombined = async () => {
    if (blockIfAiLocked()) return;
    if (blockIfSessionLoading()) return;

    try {
      const sessionKey = sessionId || "nosession";
      if (!combinedFilename) return;

      const res = await authApiService.get("/audio/temp/chunk-file", {
        params: {
          sessionId: sessionKey,
          patientId: "",
          filename: combinedFilename,
        },
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        responseType: "blob",
      });

      const blob = res.data;
      const file = new File([blob], combinedFilename, {
        type: blob.type || "audio/webm",
      });
      await handleFileUpload(file);
    } catch (error) {
      console.error("Failed to upload combined audio file:", error);
      setError("Failed to upload combined audio file. Please try again.");
    }
  };
  const onFileInputChange = (e) => {
    if (blockIfAiLocked() || blockIfSessionLoading()) {
      e.target.value = "";
      return;
    }
    const file = e.target.files?.[0];
    // Reset so picking the same file again (e.g. retry after a failure) still fires onChange.
    // The File object we already hold stays valid.
    e.target.value = "";
    if (file) {
      handleFileUpload(file);
    }
  };

  useEffect(() => {
    const openFilePicker = () => {
      if (blockIfAiLocked()) return;
      if (blockIfSessionLoading()) return;
      fileInputRef.current?.click();
    };
    window.addEventListener("vbot-open-upload-file-picker", openFilePicker);
    return () => {
      window.removeEventListener("vbot-open-upload-file-picker", openFilePicker);
    };
  }, [isAiFeatureLocked, getAccountLimitMessage, isSessionLoading]);

  const handleStartRecording = async () => {
    if (blockIfAiLocked()) return;

    // Check if maintenance mode is enabled
    if (process.env.REACT_APP_MAINTENANCE_MODE === 'true') {
      setError("Audio recording is currently under maintenance. Please try again later.");
      return;
    }

    if (mode === "transcribing") {
      alert("Please stop transcribing before start recording.");
      return;
    }

    // getDisplayMedia must run before other awaits so the browser keeps user activation (Mac/Windows).
    let preacquiredDisplayStream = null;
    let presolvedDisplayCapture = null;
    if (needsDisplayMediaCapture(recordingAudioMode)) {
      try {
        preacquiredDisplayStream = await acquireDisplayMediaStream();
      } catch (err) {
        toast.error(err?.message || "Screen share cancelled.");
        return;
      }

      presolvedDisplayCapture = await ensureDisplayAudio(
        preacquiredDisplayStream,
        recordingAudioMode
      );
      if (presolvedDisplayCapture?.cancelled) return;
    }

    const needsMic = recordingAudioMode === "input" || recordingAudioMode === "both";
    if (needsMic) {
      const hasPermission = await checkMicPermission(
        recordingAudioInputDeviceId || undefined
      );
      if (!hasPermission) {
        stopMediaStream(preacquiredDisplayStream);
        return;
      }
    }

    // Record-only on this tab: no server streaming (Deepgram/Azure). Live streaming is started from the Transcription tab / "Live Transcription".
    startRecording(
      () => {},
      undefined,
      "en-US",
      {
        audioSourceMode: recordingAudioMode,
        audioInputDeviceId: recordingAudioInputDeviceId || undefined,
        preacquiredDisplayStream: presolvedDisplayCapture?.usedMicOnly
          ? undefined
          : presolvedDisplayCapture?.displayStream ?? preacquiredDisplayStream ?? undefined,
        presolvedDisplayCapture: presolvedDisplayCapture || undefined,
      }
    );
    setMode("recording");
    setIsManuallyPaused(false);
  };

  const uploadTempChunk = async (file, isFinal) => {
    try {
      const sessionKey = sessionId || "nosession";
      const patientKey = ""; // can be wired to patient id if available

      const formData = new FormData();
      formData.append("file", file);
      formData.append("sessionId", sessionKey);
      formData.append("patientId", patientKey);
      formData.append("chunkIndex", String(tempChunkIndex));
      formData.append("isFinal", isFinal ? "true" : "false");

      await authApiService.post("/audio/temp/chunk", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });

      const nextIndex = tempChunkIndex + 1;
      setTempChunkIndex(nextIndex);
      if (typeof window !== "undefined") {
        localStorage.setItem(
          tempAudioStateKey(sessionKey),
          JSON.stringify({
            sessionId: sessionKey,
            patientId: patientKey,
            chunkIndex: nextIndex,
          })
        );
      }

      // Refresh temp state so banner and list appear without reload
      authApiService
        .get("/audio/temp/state", {
          params: { sessionId: sessionKey, patientId: patientKey },
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        })
        .then((res) => {
          const json = res.data;
          const ok = json?.success === true || json?.statusCode === true;
          const payload = json?.data || json?.body?.data;
          if (!ok || !payload) return;
          if (payload.chunkCount > 0) {
            setHasTempRecording(true);
            setTempChunks(payload.chunks || []);
          }
          if (payload.hasCombinedFile && payload.combinedFilename) {
            setHasCombinedFile(true);
            setCombinedFilename(payload.combinedFilename);
          }
        })
        .catch(() => {});
    } catch (err) {
      console.error("Failed to upload temp audio chunk:", err);
    }
  };

  const handleStopRecording = () => {
    // Stop current recording and upload the last chunk as non-final.
    stopRecording(null, async (file, url) => {
      await uploadTempChunk(file, false);
      setShowAudio(url);
      setAudioFile(file);
      setHasUploaded(false);
      setIsManuallyPaused(false);
    });

    setMode(null);
  };

  const onPauseResumeRecording = () => {
    // For computer audio modes, treat Pause as "end current chunk & upload",
    // and Resume as "start a new recording for the next chunk".
    if (isComputerAudioMode) {
      if (!isRecording && !isManuallyPaused) return;

      if (!isManuallyPaused) {
        // Pause: stop current recorder and save a chunk (non-final)
        stopRecording(null, async (file, url) => {
          await uploadTempChunk(file, false);
          setShowAudio(url);
          setAudioFile(file);
          setHasUploaded(false);
          setIsManuallyPaused(true);
        });
      } else {
        // Resume: start a new recorder for next chunk
        handleStartRecording();
        setIsManuallyPaused(false);
      }
      return;
    }

    // For mic-only mode, keep classic pause/resume using Web Speech.
    if (!isRecording) return;
    if (isPaused) {
      resumeRecording();
    } else {
      pauseRecording();
    }
  };

  const stageLabel = STAGES[transcriptionProgress.stage] || transcriptionProgress.stage || "Processing";
  const rawMessage = transcriptionProgress.message || stageLabel;
  const progressMessage =
    !websocketConnectedRef.current &&
    (rawMessage.includes("Processing...") ||
      rawMessage.includes("Starting transcription") ||
      rawMessage.includes("live progress unavailable") ||
      rawMessage.includes("Reconnecting"))
      ? "Reconnecting… Live updates may resume once the connection is restored."
      : rawMessage;
  const progressPercent = Math.min(
    100,
    Math.max(0, Math.round(Number(transcriptionProgress.percent) || 0)),
  );

  // Keep the main upload card progress UI visible while background steps are running.
  // (Even when isLoading=false, the card would otherwise "vanish" in queued mode.)
  const isProgressActive = UPLOAD_BUSY_STAGES.includes(transcriptionProgress.stage);

  useEffect(() => {
    if (!sessionId) return;
    const sid = String(sessionId);
    const matchingBusy = jobStatuses.some((job) => {
      if (job?.sessionId == null || String(job.sessionId) !== sid) return false;
      const status = String(job.status || "").toLowerCase();
      const stage = String(job.stage || "").toLowerCase();
      return (
        status === "queued" ||
        status === "processing" ||
        UPLOAD_BUSY_STAGES.includes(stage)
      );
    });
    if (!matchingBusy) return;
    if (!isLoading && !isProgressActive) return;
    onProgressChange?.({
      sessionId: sid,
      active: true,
      stage: transcriptionProgress.stage || "",
      percent: Math.min(100, Math.max(0, Math.round(Number(transcriptionProgress.percent) || 0))),
      message: transcriptionProgress.message || "",
    });
  }, [isLoading, isProgressActive, transcriptionProgress, onProgressChange, sessionId, jobStatuses]);

  // Do not lift WebSocket streaming lines to Transcription tab / header (upload tab stays “progress only”).
  useEffect(() => {
    onUploadTranscriptionStreamState?.({ active: false, lines: [], status: "" });
  }, [onUploadTranscriptionStreamState]);

  const handleCombineAndUpload = async () => {
    if (blockIfAiLocked()) return;
    if (blockIfSessionLoading()) return;

    try {
      const sessionKey = sessionId || "nosession";

      // 1) Ask backend to combine all existing chunks (if any)
      const combineRes = await authApiService.post(
        "/audio/temp/combine",
        {
          sessionId: sessionKey,
          patientId: "",
        },
        {
          headers: { Authorization: `Bearer ${token || ""}` },
        }
      );

      const combineJson = combineRes.data;
      const combineOk =
        combineJson?.success === true || combineJson?.statusCode === true;
      const combinePayload =
        combineJson?.data || combineJson?.body?.data || combineJson;

      if (!combineOk || !combinePayload) {
        setError(
          "Failed to combine saved chunks for transcription. Please try again."
        );
        return;
      }

      // Keep UI state in sync
      if (combinePayload.chunkCount > 0) {
        setHasTempRecording(true);
        setTempChunks(combinePayload.chunks || []);
      } else {
        setHasTempRecording(false);
        setTempChunks([]);
      }

      const combinedName = combinePayload.combinedFilename || "combined.webm";
      setHasCombinedFile(true);
      setCombinedFilename(combinedName);

      // 2) Directly fetch the combined file blob using the name we just got
      const fileRes = await authApiService.get("/audio/temp/chunk-file", {
        params: {
          sessionId: sessionKey,
          patientId: "",
          filename: combinedName,
        },
        headers: { Authorization: `Bearer ${token || ""}` },
        responseType: "blob",
      });

      const blob = fileRes.data;
      const file = new File([blob], combinedName, {
        type: blob.type || "audio/webm",
      });

      // 3) Reuse existing upload+WebSocket transcription flow
      await handleFileUpload(file);
    } catch (error) {
      console.error("Failed to combine & upload temp audio chunks:", error);
      setError(
        "Failed to combine saved chunks for transcription. Please try again."
      );
    }
  };

  const handleUploadClick = () => {
    try {
      if (!audioFile) {
        console.error("No audio file to upload");
        return;
      }

      // Mark last chunk as final and trigger server-side combination
      const sessionKey = sessionId || "nosession";
      const patientKey = "";
      const finalFormData = new FormData();
      finalFormData.append("file", audioFile);
      finalFormData.append("sessionId", sessionKey);
      finalFormData.append("patientId", patientKey);
      finalFormData.append("chunkIndex", String(tempChunkIndex));
      finalFormData.append("isFinal", "true");

      authApiService
        .post("/audio/temp/chunk", finalFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        })
        .then(() => {
          // After backend combines, proceed with normal upload for transcription
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(audioFile);
          fileInputRef.current.files = dataTransfer.files;
          fileInputRef.current.dispatchEvent(
            new Event("change", { bubbles: true })
          );
          setHasUploaded(true);

          if (typeof window !== "undefined") {
            localStorage.removeItem(tempAudioStateKey(sessionKey));
            localStorage.removeItem("vbot_temp_audio_state");
          }
        })
        .catch((error) => {
          console.error("Failed to finalize temp audio:", error);
        });
    } catch (error) {
      console.error("Failed to upload audio:", error);
    }
  };
  useEffect(()=>{
  setLiveTranscribed(false);
  },[])

  return (
    <>
    <Card className="border-2 border-gray-200">
      <div className="hidden lg:flex md:flex items-center justify-between">
        <CardHeader>
          <CardTitle>Upload or Record Audio</CardTitle>
          <CardDescription>
            Upload an audio file or record directly to transcribe with grammar
            correction
          </CardDescription>
        </CardHeader>
        {showAudio && !hasUploaded && (
          <div className="flex items-center gap-2 mr-10">
            <div className="rounded-full overflow-hidden  border-2 border-gray-300 shadow-md">
              <audio controls src={showAudio} />
            </div>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleUploadClick}
                    size="lg"
                    disabled={uploadDisabled}
                    className="bg-gradient-to-b from-emerald-400 to-white-700 !rounded-full shadow-lg shadow-emerald-300/60 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAiFeatureLocked && (
                      <Lock className="stroke-gray-600 font-bold stroke-[4] !w-4 !h-4 mr-0.5" />
                    )}
                    <Upload className="stroke-emerald-800 font-bold stroke-[4] !w-5 !h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Upload</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
        <div className="flex items-center gap-2">
          {!isRecording && !isManuallyPaused && (
            <>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleStartRecording}
                disabled={isAiFeatureLocked}
              >
                {isAiFeatureLocked && <Lock className="w-4 h-4" />}
                <Mic className="w-4 h-4" />
                Start recording
              </Button>
              <Button
                type="button"
                variant="outline"
                className="mr-2 text-blue-700 border-blue-300 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleCombineAndUpload}
                disabled={uploadDisabled || !hasTempRecording || tempChunks.length === 0}
              >
                Combine &amp; upload chunks
              </Button>
            </>
          )}
          {!isRecording && isManuallyPaused && (
            <>
              <Button
                className="bg-amber-500 hover:bg-amber-600 text-white mr-2"
                onClick={onPauseResumeRecording}
                disabled={isAiFeatureLocked}
              >
                Resume
              </Button>
              <Button
                type="button"
                variant="outline"
                className="mr-2 text-blue-700 border-blue-300 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleCombineAndUpload}
                disabled={uploadDisabled || !hasTempRecording || tempChunks.length === 0}
              >
                Combine &amp; upload chunks
              </Button>
            </>
          )}
          {isRecording && (
            <>
              <Button
                className="bg-amber-500 hover:bg-amber-600 text-white mr-2"
                onClick={onPauseResumeRecording}
              >
                {isPaused || isManuallyPaused ? "Resume" : "Pause"}
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white mr-2"
                onClick={handleStopRecording}
              >
                <Square className="w-4 h-4" />
                Stop
              </Button>
            </>
          )}
          <Clock className="w-4 h-4" />
          <span className="me-3">{recordingTime}</span>
        </div>
      </div>
      <CardContent>
        {!isProgressActive && !isLoading ? (
          <>
            {combineStatus && (
              <div className="mb-2 text-sm text-blue-600">
                {combineStatus}
              </div>
            )}
            {/* Intentionally no "background jobs" box.
                Users should rely on the main upload card progress UI for their current session. */}
            {hasCombinedFile && combinedFilename ? (
              <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-700">
                <div className="mb-2">
                  A combined audio file is ready for this session. You can review it and upload
                  for transcription.
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <audio
                    controls
                    className="h-8 w-full sm:w-auto"
                    src={`${process.env.REACT_APP_API_URL}/audio/temp/chunk-file?sessionId=${encodeURIComponent(
                      sessionId || "nosession"
                    )}&patientId=&filename=${encodeURIComponent(combinedFilename)}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="sm:ml-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleUploadCombined}
                    disabled={uploadDisabled}
                  >
                    {isAiFeatureLocked && <Lock className="w-4 h-4 mr-1" />}
                    Upload combined file
                  </Button>
                </div>
              </div>
            ) : hasTempRecording && (
              <div className="mb-4 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-700">
                You have an unfinished computer recording for this session.
                Below are the saved chunks. You can continue recording more chunks
                and then click Upload to combine everything into a single audio file for transcription.
              </div>
            )}
            {hasTempRecording && tempChunks.length > 0 && (
              <div className="mb-4 border rounded-md border-gray-200 bg-gray-50 p-3 space-y-2">
                <div className="flex items-center justify-between text-sm font-semibold text-gray-700 mb-1">
                  <span>Saved chunks for this session</span>
                </div>
                <ul className="text-sm text-gray-700 space-y-2 max-h-40 overflow-y-auto">
                  {tempChunks.map((chunk, idx) => (
                    <li
                      key={chunk.filename || idx}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="flex items-center gap-2">
                        <span>
                          Chunk {idx + 1}: {chunk.filename}
                        </span>
                        <audio
                          controls
                          className="h-7"
                          src={`${process.env.REACT_APP_API_URL}/audio/temp/chunk-file?sessionId=${encodeURIComponent(
                            sessionId || "nosession"
                          )}&patientId=&filename=${encodeURIComponent(
                            chunk.filename
                          )}`}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={async () => {
                          try {
                            const res = await authApiService.post(
                              "/audio/temp/chunk-delete",
                              {
                                sessionId: sessionId || "nosession",
                                patientId: "",
                                filename: chunk.filename,
                              }
                            );
                            const json = res.data;
                            const ok =
                              json?.success === true ||
                              json?.statusCode === true;
                            const payload = json?.data || json?.body?.data;
                            if (ok && payload) {
                              if (payload.chunkCount > 0) {
                                setHasTempRecording(true);
                                setTempChunks(payload.chunks || []);
                              } else {
                                setHasTempRecording(false);
                                setTempChunks([]);
                              }

                              if (
                                payload.hasCombinedFile &&
                                payload.combinedFilename
                              ) {
                                setHasCombinedFile(true);
                                setCombinedFilename(
                                  payload.combinedFilename
                                );
                              } else {
                                setHasCombinedFile(false);
                                setCombinedFilename(null);
                              }
                            }
                          } catch (error) {
                            console.error(
                              "Failed to delete temp audio chunk:",
                              error
                            );
                          }
                        }}
                        aria-label={`Delete chunk ${idx + 1}`}
                      >
                        ✕
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <RecordingOptions
              audioSourceMode={recordingAudioMode}
              onAudioSourceModeChange={setRecordingAudioMode}
              audioInputDeviceId={recordingAudioInputDeviceId}
              onAudioInputDeviceChange={setRecordingAudioInputDeviceId}
              getAudioInputDevices={getAudioInputDevices}
              className="mb-6"
            />
            <div className="text-center py-8 space-y-4">
              {/* Manual Upload */}
              <div>
                <Upload size={48} className="mx-auto mb-2 text-gray-300" />
                <input
                  type="file"
                  accept="audio/*"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={onFileInputChange}
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    if (blockIfAiLocked()) return;
                    if (blockIfSessionLoading()) return;
                    fileInputRef.current?.click();
                  }}
                  disabled={uploadDisabled}
                  className="border-2 border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAiFeatureLocked && <Lock className="w-4 h-4 mr-2" />}
                  Upload Audio File
                </Button>
                <p className="text-sm text-gray-500 mt-1">
                  Supported: MP3, WAV, M4A
                </p>
              </div>
            </div>

            {transcript && (
              <div className="mt-6 animate-fade-in-up">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">Transcription:</h3>
                  <div className="flex items-center space-x-2 animate-pulse-soft">
                    <CheckCircle size={16} className="text-green-600 shrink-0" />
                    <span className="text-sm text-green-600">
                      Ready for SOAP notes
                    </span>
                  </div>
                </div>
                <div className="p-4 border rounded-lg bg-gray-50 max-h-60 overflow-y-auto shadow-sm transition-shadow hover:shadow-md">          
                  <p className="whitespace-pre-wrap">{transcript}</p>
                </div>
              </div>
            )}
          </>
        ) : (
          <motion.div
            className="text-center py-12"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex justify-center mb-4">
              <div className="relative flex items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
                <span className="absolute inset-0 h-12 w-12 rounded-full bg-blue-400/10 animate-pulse" aria-hidden style={{ animationDuration: "2s" }} />
              </div>
            </div>
            <h3 className="text-lg font-semibold mb-2 text-gray-800">Working on your transcription</h3>
            <p className="text-2xl font-semibold text-blue-600 mb-2 tabular-nums transition-[width,opacity] duration-300 ease-out" style={{ minWidth: "3.5rem" }}>
              {progressPercent}%
            </p>
            <div className="text-sm text-gray-600 mb-4 min-h-[2.5rem] flex justify-center items-center">
              <span className="inline-block transition-opacity duration-300 ease-out">
                {progressMessage}
              </span>
            </div>
            {!websocketConnectedRef.current && progressMessage.includes("Reconnecting") && (
              <p className="text-sm text-gray-500 mb-2 max-w-md mx-auto">
                If this persists, confirm you’re signed in and your network can reach the server.
              </p>
            )}
            <div className="mt-4 bg-gray-200 rounded-full h-2.5 max-w-md mx-auto overflow-hidden shadow-inner">
              <div className="relative h-full rounded-full overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-[width] duration-300 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
                <div
                  className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent rounded-full animate-progress-shine pointer-events-none"
                  aria-hidden
                />
              </div>
            </div>
          </motion.div>
        )}
      </CardContent>
    </Card>
    {RecordingModalsGate()}
    </>
  );
}
